import React, { useEffect, useRef } from 'react';

const Thesis = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="thesis-presentation">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :root {
          --bg-primary: #fdfcfa;
          --bg-secondary: #f5f3ef;
          --text-primary: #1a1a1a;
          --text-secondary: #4a4a4a;
          --accent: #d4511e;
          --accent-light: #e8815f;
          --border: #e0ddd8;
        }

        body {
          font-family: 'Work Sans', sans-serif;
          background: var(--bg-primary);
          color: var(--text-primary);
          line-height: 1.7;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2rem;
          background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 80%;
          height: 150%;
          background: radial-gradient(circle, rgba(212, 81, 30, 0.08) 0%, transparent 70%);
          animation: float 20s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(3deg); }
          66% { transform: translate(-20px, 20px) rotate(-3deg); }
        }

        .hero-content {
          max-width: 900px;
          position: relative;
          z-index: 1;
          animation: fadeInUp 1.2s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-meta {
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--accent);
          font-weight: 600;
          margin-bottom: 1.5rem;
          animation: fadeInUp 1.2s ease-out 0.2s both;
        }

        h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.5rem, 8vw, 5rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 1.5rem;
          color: var(--text-primary);
          animation: fadeInUp 1.2s ease-out 0.4s both;
        }

        .hero-subtitle {
          font-size: clamp(1.125rem, 2.5vw, 1.5rem);
          color: var(--text-secondary);
          max-width: 700px;
          margin: 0 auto 3rem;
          animation: fadeInUp 1.2s ease-out 0.6s both;
        }

        .scroll-indicator {
          display: inline-block;
          padding: 0.75rem 1.5rem;
          border: 2px solid var(--accent);
          color: var(--accent);
          text-decoration: none;
          font-weight: 600;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          transition: all 0.3s ease;
          animation: fadeInUp 1.2s ease-out 0.8s both;
          cursor: pointer;
        }

        .scroll-indicator:hover {
          background: var(--accent);
          color: white;
          transform: translateY(-2px);
        }

        .bio-bg {
            background: var(--bg-secondary);
            border-top: 1px solid var(--border);
            border-bottom: 1px solid var(--border);
        }

        .bio-section {
          max-width: 900px;
          margin: 0 auto;
          padding: 5rem 2rem;
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 3rem;
          align-items: center;
        }

        .bio-photo {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid var(--accent);
        }

        .bio-photo-placeholder {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-light) 0%, var(--accent) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 3rem;
          font-weight: 700;
          border: 4px solid var(--accent);
        }

        .bio-content {
          flex: 1;
        }

        .bio-name {
          font-family: 'Playfair Display', serif;
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .bio-title {
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--accent);
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .bio-text {
          font-size: 1rem;
          line-height: 1.7;
          color: var(--text-secondary);
          margin: 0;
        }

        .container {
          max-width: 800px;
          margin: 0 auto;
          padding: 6rem 2rem;
        }

        .section {
          margin-bottom: 5rem;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .section:nth-child(1) { animation-delay: 0.1s; }
        .section:nth-child(2) { animation-delay: 0.2s; }
        .section:nth-child(3) { animation-delay: 0.3s; }
        .section:nth-child(4) { animation-delay: 0.4s; }
        .section:nth-child(5) { animation-delay: 0.5s; }
        .section:nth-child(6) { animation-delay: 0.6s; }

        .section-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--accent);
          font-weight: 600;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .section-label::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border);
        }

        h2 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: var(--text-primary);
        }

        p {
          font-size: 1.0625rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
        }

        .highlight {
          background: linear-gradient(to bottom, transparent 50%, rgba(212, 81, 30, 0.15) 50%);
          padding: 0 0.25rem;
        }

        .quote {
          border-left: 4px solid var(--accent);
          padding-left: 2rem;
          margin: 3rem 0;
          font-style: italic;
          font-size: 1.25rem;
          color: var(--text-primary);
        }

        .stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 2rem;
          margin: 3rem 0;
          padding: 3rem;
          background: var(--bg-secondary);
          border-radius: 4px;
        }

        .stat {
          text-align: center;
        }

        .stat-number {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--accent);
          display: block;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--text-secondary);
        }

        .timeline {
          position: relative;
          padding-left: 2rem;
          margin: 3rem 0;
        }

        .timeline::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--border);
        }

        .timeline-item {
          margin-bottom: 2.5rem;
          position: relative;
        }

        .timeline-item::before {
          content: '';
          position: absolute;
          left: -2.4rem;
          top: 0.5rem;
          width: 12px;
          height: 12px;
          background: var(--accent);
          border-radius: 50%;
          border: 3px solid var(--bg-primary);
        }

        .timeline-year {
          font-weight: 600;
          color: var(--accent);
          margin-bottom: 0.5rem;
        }

        .timeline-content {
          color: var(--text-secondary);
          line-height: 1.7;
        }

        footer {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--bg-secondary);
          border-top: 1px solid var(--border);
        }

        footer p {
          color: var(--text-secondary);
          font-size: 0.9375rem;
        }

        .download-btn {
          display: inline-block;
          margin-top: 1.5rem;
          padding: 1rem 2.5rem;
          background: var(--accent);
          color: white;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.9375rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          transition: all 0.3s ease;
          border-radius: 2px;
        }

        .download-btn:hover {
          background: var(--accent-light);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(212, 81, 30, 0.25);
        }

        @media (max-width: 768px) {
          .bio-section {
            grid-template-columns: 1fr;
            text-align: center;
            gap: 2rem;
            padding: 3rem 1.5rem;
          }

          .bio-photo,
          .bio-photo-placeholder {
            margin: 0 auto;
          }

          .container {
            padding: 4rem 1.5rem;
          }

          .stats {
            grid-template-columns: 1fr;
            padding: 2rem;
          }

          .quote {
            font-size: 1.125rem;
            padding-left: 1.5rem;
          }
        }
      `}</style>

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Work+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Hero Section */}
      <div className="hero">
        <div className="hero-content">
          <div className="hero-meta">Graduation Thesis · Class of 2026</div>
          <h1>Creating My Own Path</h1>
          <p className="hero-subtitle">
            From escaping the traditional school system to building a design career, competing as an athlete, and surviving the hardest years of my life - this is the story of learning what education really means.
          </p>
          <a href="#content" className="scroll-indicator" onClick={(e) => handleSmoothScroll(e, '#content')}>
            Read My Story
          </a>
        </div>
      </div>

      {/* Bio Section */}
      <div className="bio-bg">
        <div className="bio-section">
            {/* Replace this placeholder with: <img src="your-photo.jpg" alt="Kian Segantini" className="bio-photo" /> */}
            <div className="bio-photo-placeholder">K</div>
        
            <div className="bio-content">
                <h3 className="bio-name">Kian Segantini</h3>
                <div className="bio-title">Designer · Athlete · Self-Directed Learner</div>
                <p className="bio-text">
                I'm Kian, a 17-year-old who walked away from traditional school in 9th grade and built something better. I taught myself UX/UI design during the pandemic, turned it into paid work by 16, became a competitive swimmer, earned my lifeguard certification, and navigated family crises and legal battles, all while acing my exams. This is the story of how I created my own education, on my own terms.
                </p>
            </div>
        </div>
      </div>

      <div id="content" className="container">
        {/* Introduction */}
        <div className="section" ref={el => sectionsRef.current[0] = el}>
          <div className="section-label">Introduction</div>
          <h2>Hello, my name is Kian</h2>
          <p>My story begins in Italy, January 16th, 2009, the day I was born. I believe that every day of our lives, we learn something new, even if we are not heads buried in books. Since the day we are born, we continue learning even if we do not notice it.</p>
        </div>

        {/* Early Years */}
        <div className="section" ref={el => sectionsRef.current[1] = el}>
          <div className="section-label">Early Years</div>
          <h2>The Public School System</h2>
          <p>I started my school journey in public schools, and I have always hated it. They teach you to obey and not question authorities, to not collaborate and think for yourself, molding students into passive recipients of information rather than curious, independent thinkers. That is no surprise since they are trying to shape the next generation of workers, not leaders, but obedient and easily controllable <span className="highlight">"ants"</span> that follow orders.</p>
          <p>The only things I remember about public school, other than fake friends and bullies, are late nights studying, the nonexistent time to do what I liked, the various disciplinary reports, and the only things my father would ask me after school: "What did you study today?" and "What grade did you get?"</p>
        </div>

        {/* Pandemic */}
        <div className="section" ref={el => sectionsRef.current[2] = el}>
          <div className="section-label">2020-2021</div>
          <h2>The Pandemic: A Turning Point</h2>
          <p>Then the pandemic hit, the great COVID-19, and the DAD (Didattica a distanza, Italy's remote learning program). That is when I discovered a whole world outside the school. I started pursuing more of what I wanted to do and discovered new things. I discovered UX/UI Design. It came very naturally to me, with an artist's family background; I can say, <span className="highlight">"It runs through my blood"</span>. I also joined some online communities that taught me many core principles; I even made some friends, and thanks to one of them, who I now consider a mentor, I have learned way more than I would have ever learned in school.</p>
          <p>The pandemic was pretty "chill"; I began reading, which was something I had always pretty much hated before. I discovered instead a world of possibilities, a way to free my imagination and let it run.</p>
          <p>Most of the time, during the lessons, the boring ones especially, my friends and I played online games or messaged on a side chat, and after class, I would go outside in the backyard to play ball and attempt tricks on the hoop, or run laps around the house since I couldn't practice my sport. The beautiful part about the pandemic was that every day was a nice sunny day, with no clouds, only the blue sky and the sun. Before going to bed, I would dive into fantasy books like Harry Potter and Percy Jackson and imagine scenarios and adventures of the protagonists as if I were there with them.</p>
          <div className="quote">
            "Looking back, the pandemic gave me something school never did: the freedom to learn what I actually wanted, at my own pace, on my own terms. For the first time, I felt like I was actually growing and not just memorizing things I would forget after a test."
          </div>
        </div>

        {/* Homeschool */}
        <div className="section" ref={el => sectionsRef.current[3] = el}>
          <div className="section-label">2021-2022</div>
          <h2>Choosing Homeschool</h2>
          <p>Going back to school after the pandemic felt like stepping back into a cage, this time with face masks, restrictions, and constant headaches. I hated the masks and would pull them down whenever I could, earning warnings and threats of disciplinary notes from teachers who seemed more concerned with conformity than my actual well-being. That is when my mom asked me a question that would change everything: "Do you want to try homeschooling?"</p>
          <p>I didn't hesitate even for a second; I said: "YES, let's do this!" The idea of learning on my own terms, without the suffocating structure of traditional school, felt like freedom I had only tasted during the pandemic. No more rigid schedules, no more sitting in a classroom pretending to care about subjects that meant nothing to me. I could finally structure my days around what actually mattered: my design work, my reading, and discovering new things.</p>
          <p>Unfortunately, the road to that freedom was not as smooth as I imagined. My mom had spent the entire summer researching the best homeschool programs, sending emails, and making arrangements. My father, however, did not help at all. When he finally realized at the end of August what path I was choosing, his reaction was a furious and aggressive yelling to my mom on the phone: "He is going back to school! End of the story." The rest of that summer vacation was tense and uncomfortable, concentrated in trying to explain my choice without making him angrier. Since I was young and shy, it was difficult to defend myself or articulate why this mattered so much to me. I knew my mom believed in me, we held our ground, and when September came, I started homeschooling despite his unfounded objections.</p>
          <p>When 9th grade officially began, I had to adjust to the new type of schooling and face the challenges that would come with it. I used to have a class a day with some other students, but the classes were a lot more interactive than the traditional school ones. I had to figure out how to schedule my time for the projects teachers gave us, and create time to learn the things I was actually interested in.</p>
          <p>The year went by, and I actually felt like I had done something for myself and my future. I started dedicating more time to UX/UI Design, redesigning real websites to make them more user-friendly and intuitive. I also got back into sports, running around the block since pools were still closed, and I had no other way to do sports at that time.</p>
          <p>At the end of the year, the decisive moment came: the final exams. I studied incredibly hard to prepare a good thesis, all to demonstrate to my father that homeschooling actually works. There were two days of exams, two days of stress, where I had to focus to get everything right. When the exam day arrived, my father did not even show up for me, but it did not matter; he would have only given me anxiety. I went to the exam hall with my head up, took my exams calmly, and completed them with ease. I discovered that day that there was no need to present a thesis, even though the school indicated that it was required. The second day of exams went pretty much as the first: the examiners were "bastards" and quite against homeschoolers, but I took my exam, and when it was all over, my mom and I went to a cafe to celebrate the end of this hell with a brioche.</p>
        </div>

        {/* Stats */}
        <div className="section" ref={el => sectionsRef.current[4] = el}>
          <div className="section-label">Key Achievements</div>
          <h2>What I've Built</h2>
          <div className="stats">
            <div className="stat">
              <span className="stat-number">5</span>
              <span className="stat-label">Years Homeschooled</span>
            </div>
            <div className="stat">
              <span className="stat-number">€600</span>
              <span className="stat-label">First Paid Project</span>
            </div>
            <div className="stat">
              <span className="stat-number">∞</span>
              <span className="stat-label">Number of Possibilities</span>
            </div>
            <div className="stat">
              <span className="stat-number">6+</span>
              <span className="stat-label">Years Swimming</span>
            </div>
            <div className="stat">
              <span className="stat-number">3</span>
              <span className="stat-label">Gold Medals</span>
            </div>
            {/* <div className="stat">
              <span className="stat-number">+</span>
              <span className="stat-label">Years Swimming</span>
            </div> */}
          </div>
        </div>

        {/* Sports */}
        <div className="section" ref={el => sectionsRef.current[5] = el}>
          <div className="section-label">Sports Journey</div>
          <h2>Discipline Through Movement</h2>
          <p>During that summer, I started playing baseball with a small team near town. It was not a serious setup as they played on a makeshift U12 field in the middle of a soccer pitch, but it felt right to me. Baseball had always been part of my life in small ways; my mom used to play softball, and we would toss the ball around in the backyard when I was younger. After the pandemic, I needed to get back into sports, and with pools still requiring vaccination to get in, something outdoors was my only option. Baseball became that outlet.</p>
          <p>By September, thanks to a small tournament and having to play with another local squad, I discovered a team closer to home, one with proper fields, both U12 and U15 diamonds. I switched teams, and others followed me. There, I started making friends and building relationships with them and the coaches. It felt like having a second family. It began as an alternative sport and developed into something more serious and competitive. We would have games on weekends, and we would play summer tournaments. We were strong and united, creating memories that would last a lifetime, but everything eventually comes to an end.</p>
          <p>Time passed, and in September 2023, during a summer game, I injured my wrist while pitching and had to stop playing. Baseball was not my first love when it came to sports. Looking back, I have always been drawn to movement and challenges. Since I was little, I always participated in sports, from horseback riding to parkour to swimming, which has always been my main sport for more than six years.</p>
          <p>During the winter break, I decided to start swimming again to prepare myself for a lifeguard course where I could learn essential skills that could always be valuable throughout my future. As soon as I dove into the water, I felt like I was coming back home. The lifeguard course ran from March to May, and by the time I earned my certification on May 10th, 2025, I was not just qualified, I was hooked on being in the water again. That summer, I joined a pre-competitive training program, commuting an 8-kilometer bike ride every day just to get to the pool. It was challenging, but I loved the challenge.</p>
          <p>In the fall, after we moved to a new place, I found a local pool with a competitive swim team and tried out. I made it. Now I bike 10 kilometers a day, four times a week, just to train, and I love every second of it. On February 22nd, 2026, I competed in my first race and won gold and silver. However, I am not stopping there. As soon as I turn 18 in 2027, my goal is to compete in an Ironman 70.3. All the sports I practiced during the years taught me discipline, resilience, both physical and psychological, what it means to push past my limits, team bonding, and leadership: <span className="highlight">lessons no classroom could ever teach me.</span></p>
        </div>

        {/* Design Career */}
        <div className="section" ref={el => sectionsRef.current[6] = el}>
          <div className="section-label">Real-World Learning</div>
          <h2>From Passion to Career</h2>
          <p>While sports taught me discipline and resilience, homeschooling gave me something equally valuable: the time to turn my passion for design into real-world experience.</p>
          <p>When I started my first year in high school, I also started to change my taste in books. I transitioned from reading fantasy books to reading business books that taught me entrepreneurship, finance, and self-improvement. I wanted to understand how the real world worked, how to build my own business one day, and how to grow as a person beyond what any school professor could ever teach me.</p>
          <p>That mindset shift paid off faster than I expected. After a couple of months of reading and reinforcing my design skills, my mentor reached out to me with an opportunity: a startup founder was looking for a UX/UI Designer, and he recommended me. After a week or so, the startup founder contacted me, and after a meeting, I joined the team. While working with the startup, I learned what real collaboration looked like in a way school never could have, and designing the startup's landing page also elevated my design skills to a level I could have never imagined.</p>
          <p>After over a year with the startup, the project wrapped up, and I moved on. I started searching for freelance work, reaching out to potential clients, pitching my services, but the reality hit hard. No one was responding. I received rejection after rejection. It was frustrating, especially after the momentum I had previously built.</p>
          <p>In September 2025, after a web design agency left one of my mom's clients with an unfinished website, my mom pitched me as a replacement. The client agreed to hire me, and after a brief meeting and signing a contract, I was on my first paid job at €600 for a complete website redesign and consultation. We worked together closely, going back and forth on ideas as she explained the concept and vision for the site. Within a month of designing and reviewing, I delivered the finalized project, and the client was thrilled with the result.</p>
          <p>That project was a turning point for me. It wasn't just about the money; it was proof that my skills were valuable, that I could compete in the real world, and that all those hours teaching myself design during the pandemic had been worth it. What started as a simple curiosity in 9th grade had become a legitimate career path.</p>
        </div>

        {/* Travel */}
        <div className="section" ref={el => sectionsRef.current[7] = el}>
          <div className="section-label">Travel & Growth</div>
          <h2>Learning Beyond Books</h2>
          <p>My education was not confined to books and screens. Throughout these years, my mom and I traveled whenever we could. I mentioned at the beginning that we learn something new every day, even when we do not notice it. Nowhere has this been truer than in the travels I have taken with my mom over the years.</p>
          <p>Every trip we took taught me something new, like the culture, the food, or the history of a place. We wouldn't just go to some place to stay in a hotel and then go to a beach or shopping, we would go to live the life of the places, going to local restaurants to eat their typical food, or we would go to explore the unknown, we would take off-road paths, always ending up in beautiful places with no one around.</p>
          <p>Then sometimes we would go on more sophisticated trips, like when we tried glamping in Tuscany, and my mom got a fever as soon as we arrived. That was one of the first times I had to "make it on my own". I went exploring around the campgrounds, trying different activities such as archery, which I discovered I was really good at, or just running around. In the morning, I would prepare breakfast, be back at lunchtime to prepare lunch, and help my mom with dinner in the evening. It was the first time that I had a real taste for independence. We never gave up these adventures that allowed us to see places that only God and locals knew existed. We treated the hotel only as a home base for the night, not a constant place where we always had to be.</p>
        </div>

        {/* Hard Years */}
        <div className="section" ref={el => sectionsRef.current[8] = el}>
          <div className="section-label">The Hard Years</div>
          <h2>Resilience Through Adversity</h2>
          <p>Travel taught me independence and adaptability, but nothing could have ever prepared me for what the next few years would demand of me. Beneath all the wins, in sports, design work, and adventures, the last three years have been some of the hardest of my life.</p>
          <p>In November 2023, after my father burst into our house, threatening legal actions over financial disputes and my homeschooling path, just the day before my grandma passed away, followed by a spiral of court dates, tension that made holidays unbearable, and studying for the exams nearly impossible. The stress weighed on me, but most of all, it drained my mom.</p>
          <p>Yet, I refused to let it stop me. While dealing with lawyers and court dates, I was studying for my 10th-grade final exams, preparing a thesis, and trying to be emotionally present for my mom. I kept showing up to baseball practice, to my design projects, to my studies. In the middle of everything, the exam date arrived, and with all the stress that my father put me through in the last months, the exam stress felt like a walk in the park. It was overwhelming, but I kept going. I went into the exam hall and aced all the tests.</p>
          <p>By the summer of 2025, we had to move out of our home entirely. Even while packing up our lives and starting over, I kept training, kept working, and kept moving forward.</p>
          <div className="quote">
            "The last three years taught me something no classroom ever could: how to stay focused and keep building my life even when everything around me was falling apart. Resilience is not something you learn from a textbook; it is something you earn through tough times."
          </div>
        </div>

        {/* Present */}
        <div className="section" ref={el => sectionsRef.current[9] = el}>
          <div className="section-label">Present Day</div>
          <h2>Where I Am Now</h2>
          <p>By October 2025, we had finally settled into our new home. For the first time in years, life felt stable. Just a month after my first race, I competed in my second race and won two gold medals. Standing on that podium felt surreal, and I realized how far I had come, not just as an athlete but also as a person.</p>
          <p>Looking back on this journey, I now realize how many opportunities I have had that I would have missed if I had stayed in a traditional school. The UX/UI design work, the competitive swimming, and the connections I have made with writers like Cornelia Funke or coaches like Tony Robbins. None of these would have been possible with the mentality that traditional school imposes on you.</p>
          <p>Now I balance school studies, business, and training, with the freedom to plan my day on my own terms, making all the difference in how I face life and manage priorities.</p>
        </div>

        {/* Future */}
        <div className="section" ref={el => sectionsRef.current[10] = el}>
          <div className="section-label">Looking Ahead</div>
          <h2>My Future Goals</h2>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-year">2027</div>
              <div className="timeline-content">In 2027, as soon as I turn 18, I will be competing in an Ironman 70.3, a challenge that would have seemed impossible to even achieve to the kid in that public school classroom, but one I have dreamed of ever since my mom photographed an Ironman years ago.</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">Ongoing</div>
              <div className="timeline-content">Today, I am working towards a lifeguard position at my pool, turning the certification I earned into real work experience.</div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">Future</div>
              <div className="timeline-content">I am also building my UX/UI design business, taking on more clients and turning what started as a pandemic hobby into a legitimate career.</div>
            </div>
          </div>
          <div className="quote">
            "Education, I have learned, is not about sitting in a classroom and memorizing facts; it's about building a life on your own terms and at your own pace, learning from every experience, and never stopping or letting anything stop me. That is the education I have given myself, and it is worth more than any diplomas. I am not the same kid who hated school. I am someone who created his own path."
          </div>
        </div>
      </div>

      <footer>
        <p>Educational Biography — Kian Segantini</p>
        <p>West River Academy · Class of 2026</p>
        <a href="/assets/thesis2026.pdf" className="download-btn" target='blank'>Download Full Thesis (PDF)</a>
      </footer>
    </div>
  );
};

export default Thesis;